import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Heart, ShoppingBag, Star, Truck, ShieldCheck, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { optimizeImg } from "../lib/img";

export default function QuickViewDialog({ product, onClose, onAddToCart }) {
  const [favorite, setFavorite] = useState(false);

  // Bu dialog tüm ürünler için TEK bir bileşen örneği olarak kalıyor (parent
  // sayfalar `product` prop'unu değiştiriyor, bileşeni yeniden mount etmiyor)
  // — bu yüzden favori durumu ürün değişince elle sıfırlanmalı, yoksa bir
  // ürünü favorileyip kapatınca sıradaki ürün de favoriymiş gibi görünürdü.
  useEffect(() => {
    setFavorite(false);
  }, [product?.id]);

  if (!product) return null;

  const outOfStock = product.stock === 0;

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-4xl w-[92vw] sm:w-full p-0 bg-white border-0 rounded-3xl gap-0 max-h-[88vh] overflow-y-auto"
        data-testid="quick-view-dialog"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative aspect-[4/3] sm:aspect-square md:aspect-auto bg-[#F8F8F8] rounded-t-3xl md:rounded-t-none md:rounded-l-3xl overflow-hidden">
            <img
              src={optimizeImg(product.image, 600)}
              alt={product.name}
              width="600"
              height="600"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6 sm:p-7 md:p-10 flex flex-col">
            {product.rating != null && (
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < Math.round(product.rating) ? "fill-[#D4A15C] text-[#D4A15C]" : "text-[#EAEAEA]"
                    }`}
                  />
                ))}
                <span className="text-xs text-[#888888] ml-1">({product.rating.toFixed(1)})</span>
              </div>
            )}

            <h3 className="font-serif text-3xl text-[#222222] tracking-tight leading-tight mb-3" data-testid="quick-view-title">
              <DialogTitle asChild>
                <span>{product.name}</span>
              </DialogTitle>
            </h3>
            <DialogDescription className="sr-only">
              {product.desc}
            </DialogDescription>

            <div className="flex items-baseline gap-3 mb-5">
              <span className="font-serif text-4xl text-[#A85C34] font-semibold">
                {product.price.toLocaleString("tr-TR")}₺
              </span>
              {product.oldPrice && (
                <span className="text-lg text-[#888888] line-through">
                  {product.oldPrice.toLocaleString("tr-TR")}₺
                </span>
              )}
            </div>

            <p className="text-[#555555] leading-relaxed mb-7">{product.desc}</p>

            <ul className="space-y-2.5 mb-8">
              {[
                { icon: Truck, label: "Aynı gün teslimat — Hatay, Adana, Osmaniye, Gaziantep · Türkiye geneli kargo" },
                { icon: ShieldCheck, label: "Taze çiçek garantisi" },
                { icon: Clock, label: "7/24 sipariş hattı" },
              ].map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-[#555555]">
                  <b.icon className="w-4 h-4 text-[#A85C34]" />
                  {b.label}
                </li>
              ))}
            </ul>

            <div className="mt-auto flex gap-3">
              <button
                onClick={() => {
                  if (outOfStock) return;
                  onAddToCart(product);
                  onClose();
                }}
                disabled={outOfStock}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full px-6 py-4 font-medium transition-colors active:scale-95 disabled:bg-[#CCCCCC] disabled:cursor-not-allowed disabled:hover:bg-[#CCCCCC]"
                data-testid="quick-view-add-cart"
              >
                <ShoppingBag className="w-4 h-4" />
                {outOfStock ? "Tükendi" : "Sepete Ekle"}
              </button>
              <button
                onClick={() => setFavorite((f) => !f)}
                aria-label="Favorilere ekle"
                aria-pressed={favorite}
                className="w-14 h-14 rounded-full border border-[#EAEAEA] hover:border-[#D4A15C] flex items-center justify-center transition-colors"
              >
                <Heart className={`w-4 h-4 transition-colors ${favorite ? "fill-[#D4A15C] text-[#D4A15C]" : ""}`} />
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
